import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/InstagramCompareOverview.graphql';
import { Query, QueryinstagramCompareOverviewArgs } from '../../__generated__/globalTypes';

export const useInstagramCompareOverviewQuery = (
  options?: QueryHookOptions<Query, QueryinstagramCompareOverviewArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryinstagramCompareOverviewArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useInstagramCompareOverviewLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryinstagramCompareOverviewArgs>
): QueryResult<Query, QueryinstagramCompareOverviewArgs> & {
  getInstagramCompareOverview: LazyQueryExecFunction<Query, QueryinstagramCompareOverviewArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getInstagramCompareOverview, result] = useLazyQuery<Query, QueryinstagramCompareOverviewArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getInstagramCompareOverview, ...result };
};

export const useInstagramCompareOverviewPromiseQuery = () => {
  const client = useApolloClient();

  const getInstagramCompareOverview = useCallback(
    (options?: Omit<QueryOptions<QueryinstagramCompareOverviewArgs, Query>, 'query'>) =>
      client.query<Query, QueryinstagramCompareOverviewArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getInstagramCompareOverview };
};
// Suspense Query Hooks
export const useInstagramCompareOverviewSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryinstagramCompareOverviewArgs>
) => {
  const result = useSuspenseQuery<Query, QueryinstagramCompareOverviewArgs>(QUERY, {
    ...options
  });

  return result;
};
